import React, { useEffect, useState } from "react";
import { ModuleRegistry, ClientSideRowModelModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faTrashAlt, faEdit, faFileExport, faFile } from '@fortawesome/free-solid-svg-icons';
import { Spinner, Button, Col, Container, Form, Row, Pagination } from '@themesberg/react-bootstrap';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';

import {
  createTableRecord,
  deleteTableRecord,
  fetchTableData,
  getTableName,
  getTableSchema,
  getTranslation,
  updateTableRecord,
  exportFullTableData,
  exportSelectedTableData
} from '../../services/tablesService';
import { getUserTables } from "../../services/authService";
import ModalForm from '../components/ModalForm';
import FileModal from './FileModal';
import ComponentModal from './ComponentModal';
import './tables.css';

// Регистрируем модуль для клиентской модели строк AG Grid
ModuleRegistry.registerModules([ClientSideRowModelModule]);

// Локализация надписей для фильтров
const localeText = {
  contains: 'Содержит',
  notContains: 'Не содержит',
  equals: 'Равно',
  notEqual: 'Не равно',
  greaterThan: 'Больше',
  lessThan: 'Меньше',
  greaterThanOrEqual: 'Больше или равно',
  lessThanOrEqual: 'Меньше или равно',
  and: 'И',
  or: 'ИЛИ'
};

const Tables = () => {
  const { endpoint } = useParams();
  const [fullData, setFullData] = useState([]); // полный набор данных
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [componentId, setComponentId] = useState(null);
  const [componentField, setComponentField] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [schema, setSchema] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState('word');
  // Функция фильтрации – если требуется кастомный поиск на клиенте
  const doCustomFilter = (query) => {
    if (!query) {
      setRowData(fullData);
      return;
    }
    const lowerQuery = query.toLowerCase();
    const filtered = fullData.filter(row => {
      return Object.values(row).some(val => {
        if (val === null || val === undefined) return false;
        return String(val).toLowerCase().includes(lowerQuery);
      });
    });
    setRowData(filtered);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    doCustomFilter(query);
  };

  const handleComponentSave = async (id, field, data) => {
    try {
      await updateTableRecord(endpoint, id, { [field]: data });
      loadTableData();
    } catch (error) {
      console.error('Ошибка при сохранении компонента:', error);
    }
  };

  const checkIfTableIsEditable = async () => {
    try {
      const editableTables = await getUserTables();
      const isCurrentTableEditable = editableTables.some(table => `${table.modelName}s` === endpoint);
      setIsEditable(isCurrentTableEditable);
    } catch (error) {
      console.error('Ошибка при проверке доступности таблицы для редактирования:', error);
    }
  };

  const flattenAttributes = (item, openFileModal, openComponentModal) => {
    const result = { id: item.id };
    const attributes = item.attributes;
    let pinnedHeaders = [];
    for (const key in attributes) {
      if (attributes.hasOwnProperty(key)) {
        const value = attributes[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
          if (value[0].hasOwnProperty('id')) {
            result[key] = (
              <Button onClick={() => openComponentModal(item.id, key)} variant="outline-primary">
                Изменить <FontAwesomeIcon icon={faEdit} />
              </Button>
            );
            pinnedHeaders = [key, ...pinnedHeaders];
          } else {
            result[key] = (
              <Button onClick={() => openFileModal(item.id, key)} variant="outline-primary">
                Посмотреть <FontAwesomeIcon icon={faEdit} />
              </Button>
            );
            pinnedHeaders = [key, ...pinnedHeaders];
          }
        } else if (typeof value === 'object' && value !== null) {
          if (value.hasOwnProperty('data')) {
            result[key] = (
              <Button onClick={() => openFileModal(item.id, key)} variant="outline-primary">
                Посмотреть <FontAwesomeIcon icon={faEdit} />
              </Button>
            );
          } else {
            result[key] = (
              <Button onClick={() => openComponentModal(item.id, key)} variant="outline-primary">
                Изменить <FontAwesomeIcon icon={faEdit} />
              </Button>
            );
          }
          pinnedHeaders = [key, ...pinnedHeaders];
        } else {
          result[key] = value !== null ? value : "Нет данных";
        }
      }
    }
    return { result, pinnedHeaders };
  };

  const openFileModal = (id, field) => {
    setFileData({ id, field });
    setShowFileModal(true);
  };

  const openComponentModal = (id, field) => {
    setComponentId(id);
    setComponentField(field);
    setShowComponentModal(true);
  };

  const openModal = async (data) => {
    setEditingData(data);
    setShowModal(true);
    try {
      const schemaData = await getTableSchema(endpoint);
      setSchema(schemaData);
    } catch (error) {
      console.error('Ошибка при загрузке схемы:', error);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingData(null);
  };

  const handleFileModalClose = () => {
    setShowFileModal(false);
    setFileData(null);
  };

  const handleComponentModalClose = () => {
    setShowComponentModal(false);
    setComponentId(null);
    setComponentField(null);
  };

  const handleCellValueChange = async (params) => {
    const updatedData = params.data;
    const oldValue = params.oldValue;
    const newValue = params.newValue;
    if (oldValue === newValue) return;
    let changedField = { [params.colDef.field]: newValue };
    if (params.colDef.cellEditor === 'agDateCellEditor' && newValue) {
      changedField[params.colDef.field] = moment(newValue).format('YYYY-MM-DD');
    }
    try {
      await updateTableRecord(endpoint, updatedData.id, { ...changedField });
    } catch (error) {
      console.error('Ошибка при обновлении записи:', error);
    }
  };

  const fetchDisplayName = async () => {
    try {
      const data = await getTableName(endpoint);
      setDisplayName(data.displayName || 'Название таблицы');
    } catch (error) {
      setDisplayName('Название таблицы');
    }
  };

  // Загрузка данных с сервера без применения поиска
  const loadTableData = async (page = 1, pageSize = 10) => {
    let data = [];
    let pinnedH = [];
    const response = await fetchTableData(endpoint, page, pageSize);

    if (response && Array.isArray(response.data)) {
      data = response.data.map(item => {
        const { result, pinnedHeaders } = flattenAttributes(item, openFileModal, openComponentModal);
        pinnedH = [...pinnedH, ...pinnedHeaders];
        return result;
      });
    }

    if (data.length > 0) {
      const schemaData = await getTableSchema(endpoint);
      const attributes = schemaData.attributes;

      //  Получаем порядок колонок из схемы
      const orderedKeys = Object.keys(attributes);

      //  Создаем `columnDefs` в нужном порядке
      const translatedHeaders = await Promise.all(
        orderedKeys
          .filter(key => !['id','publishedAt', 'updatedAt', 'createdAt','createdBy','updatedBy'].includes(key))
          .map(async (key) => {
            const attribute = attributes[key];
            const translation = await getTranslation(key);
            let columnDef = {
              headerName: translation.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
              field: key,
              pinned: pinnedH.includes(key) ? 'right' : null,
              editable: key !== 'id',
              width: pinnedH.includes(key) ? 150 : undefined,
              headerClass: 'multi-line-header',
              sortable: true,
              filter: true,
              resizable: true,
              ...(key === "order_number" ? { sort: 'asc' } : {}),
            };

            if (attribute?.type) {
              switch (attribute.type) {
                case 'string':
                  columnDef = {
                    ...columnDef,
                    cellEditor: 'agTextCellEditor',
                    filter: 'agTextColumnFilter',
                    filterParams: {
                      filterOptions: ['contains'],
                      defaultOption: 'contains',
                      suppressAndOrCondition: true,
                    },
                  };
                  break;
                case 'boolean':
                  columnDef = { ...columnDef, cellEditor: 'agCheckboxCellEditor' };
                  break;
                case 'integer':
                case 'float':
                  columnDef = {
                    ...columnDef,
                    cellEditor: 'agNumberCellEditor',
                    filter: 'agNumberColumnFilter',
                    filterParams: {
                      filterOptions: ['equals'],
                      defaultOption: 'equals',
                      suppressAndOrCondition: true,
                    },
                  };
                  break;
                case 'datetime':
                  columnDef = {
                    ...columnDef,
                    cellEditor: 'agDateCellEditor',
                    valueFormatter: params => new Date(params.value).toLocaleString(),
                    filter: 'agDateColumnFilter',
                    filterParams: {
                      filterOptions: ['equals'],
                      defaultOption: 'equals',
                      suppressAndOrCondition: true,
                    },
                  };
                  break;
                case 'media':
                  columnDef = {
                    ...columnDef,
                    cellRenderer: params => {
                      if (Array.isArray(params.value)) {
                        return params.value.map((file, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                            <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ marginRight: '5px' }}>
                              {file.name || `File ${index + 1}`}
                            </a>
                            <Button onClick={() => openFileModal(params.data.id, key)}
                                    variant="outline-primary" size="sm">
                              Посмотреть <FontAwesomeIcon icon={faFile} />
                            </Button>
                          </div>
                        ));
                      }
                      return (
                        <Button onClick={() => openFileModal(params.data.id, key)}
                                variant="outline-primary" size="sm">
                          Добавить <FontAwesomeIcon icon={faFile} />
                        </Button>
                      );
                    },
                  };
                  break;
                case 'enumeration':
                  columnDef = {
                    ...columnDef,
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: { values: attribute.enum },
                  };
                  break;
                case 'component':
                  columnDef = {
                    ...columnDef,
                    cellRenderer: params => (
                      <Button onClick={() => openComponentModal(params.data.id, key)}
                              variant="outline-primary" size="sm">
                        Изменить <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    ),
                  };
                  break;
                case 'relation':
                  columnDef = { ...columnDef, valueFormatter: params => params.value?.id || 'No Relation' };
                  break;
                case 'date':
                  columnDef = {
                    ...columnDef,
                    cellEditor: 'agDateCellEditor',
                    valueFormatter: params => params.value ? new Date(params.value).toLocaleDateString() : 'Нет данных',
                    valueParser: params => params.newValue ? moment(params.newValue, 'DD/MM/YYYY').toISOString() : null,
                    filter: 'agDateColumnFilter',
                    filterParams: {
                      filterOptions: ['equals'],
                      defaultOption: 'equals',
                      suppressAndOrCondition: true,
                    },
                  };
                  break;
                default:
                  break;
              }
            }
            return columnDef;
          })
      );

      // 👉 Добавляем чекбокс в начало
      translatedHeaders.unshift({
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
        filter: false,
        sortable: false,
        headerClass: 'ag-header-cell-comp-wrapper-hide',
        resizable: false,
        pinned: 'left'
      });

      // 👉 Устанавливаем колонки в правильном порядке
      setColumnDefs(translatedHeaders);
    }

    setFullData(data);
    setRowData(data);
    setTotalPages(Math.ceil(response.meta.pagination.total / pageSize));
  };


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await checkIfTableIsEditable();
        await loadTableData();
        await fetchDisplayName();
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [endpoint]);

  const handleSearch = () => {
    setPage(1);
    loadTableData(1, pageSize);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadTableData(newPage, pageSize);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(e.target.value);
    setPage(1);
    loadTableData(1, e.target.value);
  };

  const handleSave = async (data) => {
    try {
      if (editingData?.id) {
        await updateTableRecord(endpoint, editingData.id, { data });
      } else {
        await createTableRecord(endpoint, data);
      }
      loadTableData();
      handleModalClose();
    } catch (error) {
      console.error('Ошибка при сохранении записи:', error);
    }
  };

  const onSelectionChanged = (event) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const deleteSelectedRows = async () => {
    try {
      await Promise.all(selectedRows.map(row => deleteTableRecord(endpoint, row.id)));
      loadTableData();
    } catch (error) {
      console.error('Ошибка при удалении записей:', error);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportFullTableData(endpoint, '*', exportFormat);
      const fileExtension = exportFormat === 'excel' ? 'xlsx' : 'docx';
      saveAs(blob, `${endpoint}-export.${fileExtension}`);
    } catch (error) {
      console.error('Ошибка при экспорте данных:', error);
    }
  };

  const handleExportSelected = async () => {
    const selectedIds = selectedRows.map(row => row.id);
    if (selectedIds.length === 0) {
      alert('Выберите строки для экспорта');
      return;
    }
    try {
      const blob = await exportSelectedTableData(endpoint, selectedIds, '*', exportFormat);
      const fileExtension = exportFormat === 'excel' ? 'xlsx' : 'docx';
      saveAs(blob, `${endpoint}-selected-export.${fileExtension}`);
    } catch (error) {
      console.error('Ошибка при экспорте выбранных данных:', error);
    }
  };


  return (
    <article>
      <Container className="px-0 w-100" style={{ maxWidth: "100%" }}>
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <Row className="d-flex flex-wrap flex-md-nowrap align-items-center py-4">
              <Col className="d-block mb-4 mb-md-0">
                <h1 className="h2">{displayName}</h1>
              </Col>
              {isEditable && (
                <Col className="d-flex justify-content-end">
                  <Button variant="primary" className={'me-2'} onClick={() => openModal(null)}>
                    <FontAwesomeIcon icon={faPlusCircle} className="me-2" /> Добавить новую запись
                  </Button>
                  <Button variant="danger" onClick={deleteSelectedRows} disabled={selectedRows.length === 0}>
                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" /> Удалить выбранные
                  </Button>
                </Col>
              )}
            </Row>

            {/* Кастомный поиск: при изменении searchQuery данные фильтруются на клиенте */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Control
                  type="text"
                  placeholder="Поиск..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Col>
              <Col md={2}>
                <Button variant="primary" onClick={handleSearch}>Обновить данные</Button>
              </Col>
            </Row>

            <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 200px)', width: '100%' }}>
              <AgGridReact
                onGridReady={(params) => {}}
                rowData={rowData}
                columnDefs={columnDefs}
                rowSelection="multiple"
                onSelectionChanged={onSelectionChanged}
                defaultColDef={{
                  editable: true,
                  sortable: true,
                  filter: true,
                  resizable: true,
                  autoHeaderHeight: true,
                }}
                localeText={localeText}
                onCellValueChanged={handleCellValueChange}
              />
            </div>

            <Row className="mt-3">
              <Col md={3}>
                <Form.Select value={pageSize} onChange={handlePageSizeChange}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={10000000}>Все</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Pagination>
                  {[...Array(totalPages).keys()].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === page}
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                </Pagination>
              </Col>
            </Row>


            <Row className="mt-3">
              <Col md={3}>
                <Form.Group controlId="exportFormat">
                  <Form.Label>Формат экспорта</Form.Label>
                  <Form.Select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                  >
                    <option value="word">Word (.docx)</option>
                    <option value="excel">Excel (.xlsx)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3 justify-content-end">
              <Col md={1.5} className="mb-2">
                <Button variant="primary" onClick={handleExport}>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" /> Экспорт всех
                </Button>
              </Col>
              <Col md={1.5}>
                <Button variant="primary" onClick={handleExportSelected}>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" /> Экспорт выбранных
                </Button>
              </Col>
            </Row>

            {showModal && (
              <ModalForm
                show={showModal}
                handleClose={handleModalClose}
                data={editingData}
                schema={schema}
                onSave={handleSave}
              />
            )}

            {showFileModal && (
              <FileModal
                show={showFileModal}
                handleClose={handleFileModalClose}
                data={fileData}
              />
            )}

            {showComponentModal && (
              <ComponentModal
                show={showComponentModal}
                handleClose={handleComponentModalClose}
                endpoint={endpoint}
                id={componentId}
                field={componentField}
                onSave={handleComponentSave}
              />
            )}
          </>
        )}
      </Container>
    </article>
  );
};

export default Tables;
